import BookingDetails from '@components/BookingDetails/BookingDetails';
import {
  InnerContainer,
  RightContainer,
  StepContainer,
} from '@components/Steps/Steps.styles';
import {
  InformationIcon,
  LuxTypography,
  Stack,
  useTheme,
} from '@gemini/fe-ds-core';
import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ThankYouMessageWrapper } from '@components/ThankYouPage/ThankYouPage.styles';
import useGetAppointmentData from '@hooks/useGetAppointmentData';
import { usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { RescheduleCard, RescheduleWrapper } from './RescheduleFlow.styles';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import { Helmet } from 'react-helmet';
import { isEmbeddedIntegrationMode } from '@utils/enums/IntegrationModes.enum';
import { BrandNameList } from '@utils/consts/brandNameList';
import { PostSubmissionError } from '@utils/enums/PostSubmissionError.enum';

export enum RescheduleSteps {
  RESCHEDULE,
  RESCHEDULE_SCHEDULE_EXAM,
  RESCHEDULE_CONFIRMED,
}

const ReschedulePage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const brandName = useOnlineBookingStore(state => state.brandName);
  const integrationMode = useOnlineBookingStore(state => state.integrationMode);

  const appointmentIdFromUrl = useMemo(() => {
    const queryString = isEmbeddedIntegrationMode(integrationMode)
      ? globalThis.location.hash.split('?')[1] || ''
      : globalThis.location.search.substring(1);

    return new URLSearchParams(queryString).get('appointmentId');
  }, [integrationMode]);

  const errorParamFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(
      globalThis.location.search.substring(1),
    );
    const hashParams = new URLSearchParams(
      globalThis.location.hash.split('?')[1] || '',
    );

    return (searchParams.get('error') ??
      hashParams.get('error')) as PostSubmissionError;
  }, []);

  const appointmentId =
    useOnlineBookingStore(state => state.appointmentId) || appointmentIdFromUrl;
  const { data } = useGetAppointmentData({ appointmentId });
  const updatePatient = usePatientStore(state => state.updatePatient);
  const setCurrentPatient = useOnlineBookingStore(
    state => state.setCurrentPatient,
  );

  useEffect(() => {
    setCurrentPatient(0);
  }, [setCurrentPatient]);

  useEffect(() => {
    if (data) {
      console.log('##data', data);
      const appointmentExam = data?.AppointmentExam;
      const insurance = data?.insurance;

      updatePatient(0, FormIds.AppointmentType, {
        appointmentId: appointmentId || undefined,
        appointmentType: {
          name:
            brandName && data?.idTranslation
              ? t(
                  `appointmentTypePage.${brandName}.${data.idTranslation}.title`,
                )
              : data?.appointmentType || '',
          idTranslation: data?.idTranslation,
          appointmentType: data?.appointmentTypeId || '',
          isModified: false,
        },
        insurance: {
          type: insurance?.type || undefined,
          id: insurance?.id || '',
          name: insurance?.name || undefined,
        },
        scheduleExam: {
          day: appointmentExam?.date || '',
          hour: appointmentExam?.startTime || '',
          doctor: appointmentExam?.doctorName || '',
        },
        //save the date also in previousSlotToDisable
        previousSlotToDisable: {
          day: appointmentExam?.date || '',
          hour: appointmentExam?.startTime || '',
          doctor: appointmentExam?.doctorName || '',
        },
        patientId: appointmentExam?.patientId, // settato a questo livello solo per il reschedule
      });
    }
  }, [appointmentId, brandName, data, t, updatePatient]);

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_reschedule_title')}</title>
      </Helmet>
      <RescheduleWrapper>
        <ThankYouMessageWrapper>
          <Stack
            style={{
              width: '2.75rem',
              height: '2.75rem',
              borderRadius: '100%',
              border: `1px solid ${theme.palette.LUX.obTokens.bordersColorFunctionalInfo}`,
              backgroundColor: theme.palette.LUX.obTokens.bgColorFunctionalInfo,
            }}
            justifyContent="center"
            alignItems="center"
          >
            <InformationIcon color={theme.palette.LUX.content.primary} />
          </Stack>
          <LuxTypography
            variant="heading3"
            fontWeight={brandName === BrandNameList.TO ? 700 : 400}
          >
            {t('manageAppointment.manage')}
          </LuxTypography>
        </ThankYouMessageWrapper>
        <InnerContainer>
          <StepContainer
            borderColor={
              theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
            }
          >
            <CardTitleWrapper
              borderColor={
                theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
              }
            >
              <LuxTypography variant="heading4">
                {t('rescheduleAppointment.title')}
              </LuxTypography>
            </CardTitleWrapper>
            <RescheduleCard>
              <LuxTypography
                variant={'heading4'}
                fontSize={'1.125rem'}
                fontWeight={400}
              >
                {t('rescheduleAppointment.reschedule_card_title')}
              </LuxTypography>
              {errorParamFromUrl &&
                [
                  PostSubmissionError.SLOT_NOT_AVAILABLE,
                  PostSubmissionError.PAST,
                  PostSubmissionError.LESS_24_HR,
                ].includes(errorParamFromUrl) && (
                  <LuxTypography variant={'textXs'} fontSize={'0.875rem'}>
                    {t('rescheduleAppointment.error_slot_not_available')}
                  </LuxTypography>
                )}
              <LuxTypography variant={'textXs'} fontSize={'0.875rem'}>
                {t('rescheduleAppointment.reschedule_card_descr')}
              </LuxTypography>
            </RescheduleCard>
          </StepContainer>
          <RightContainer manageOrRescheduleMode>
            <BookingDetails hideEditButton manageOrRescheduleMode />
          </RightContainer>
        </InnerContainer>
      </RescheduleWrapper>
    </>
  );
};

export const useRescheduleMeta = (integrationMode?: string) => {
  const appointmentIdFromUrl = useMemo(() => {
    const queryString = isEmbeddedIntegrationMode(integrationMode)
      ? globalThis.location.hash.split('?')[1] || ''
      : globalThis.location.search.substring(1);

    return new URLSearchParams(queryString).get('appointmentId');
  }, [integrationMode]);
  const appointmentId =
    useOnlineBookingStore(state => state.appointmentId) || appointmentIdFromUrl;

  const { data } = useGetAppointmentData({ appointmentId });
  return {
    reschedulable: data?.reschedulable,
    appointmentStatus: data?.appointmentStatus,
  };
};

export default ReschedulePage;
